"use client"

import DynamicLink from "@/app/(site)/_components/Molecules/Link"
import { hasVisibleText } from "@/src/utils/a11y/hasVisibleText"
import { headingLevelRank, remapAuthorHeadingLevel } from "@/src/utils/a11y/headingLevel"
import { clsx } from "@/src/utils/clsx"
import { isInternalLink } from "@/src/utils/string/parseCentraLinks"
import { type HeadingLevel } from "@/types/gc-modules-types"
import { createElement, type ReactNode, useMemo } from "react"

interface CentraRichTextProps {
    content: string
    className?: string
    baseHeadingLevel?: HeadingLevel
}

const LINK_CLASS = "underline hover:text-col-text-primary"

// The renderer only ever emits these elements. Anything else an author pastes from Centra
// (inline styles, classes, tables, <script>, etc.) is unwrapped — its text is kept, the tag
// is dropped — so unsafe or off-design markup can never reach the DOM. This is the allowlist
// that used to be enforced by DOMPurify, now enforced by construction.
const INLINE_TAGS = new Set(["strong", "em", "u", "b", "i", "s", "strike", "del", "span"])
const BLOCK_TAGS = new Set(["p", "h2", "h3", "h4", "ul", "ol", "li"])
const AUTHOR_HEADING_TAGS = new Set(["h2", "h3", "h4"])
const AUTHOR_HEADING_TAG_PATTERN = /<h([2-4])\b/gi

function minHeadingInHtml(html: string): HeadingLevel | null {
    let min: HeadingLevel | null = null
    let match: RegExpExecArray | null
    AUTHOR_HEADING_TAG_PATTERN.lastIndex = 0

    while ((match = AUTHOR_HEADING_TAG_PATTERN.exec(html)) !== null) {
        const level = `h${match[1]}` as HeadingLevel
        if (!min || headingLevelRank(level) < headingLevelRank(min)) min = level
    }

    return min
}
const VOID_TAGS = new Set(["br"])

// The base rich-text container renders the light Trade Gothic face at weight 300, and Tailwind
// preflight only nudges <b>/<strong> to `bolder` (≈400 against a 300 face) — so semantic
// formatting tags barely read. We pin each formatting tag to an explicit, design-correct style:
// bold swaps to the dedicated bold font family, the rest use plain text-decoration / font-style.
const TAG_CLASS: Record<string, string> = {
    strong: "font-tradeBoldNew font-700",
    b: "font-tradeBoldNew font-700",
    em: "italic",
    i: "italic",
    u: "underline",
    s: "line-through",
    strike: "line-through",
    del: "line-through"
}

// Matches an HTML tag: optional closing slash, tag name, attribute string, optional self-close.
const TAG_PATTERN = /<(\/)?([a-zA-Z][a-zA-Z0-9]*)\b([^>]*?)(\/)?>/g
const HREF_PATTERN = /href\s*=\s*"([^"]*)"/i
const TITLE_PATTERN = /title\s*=\s*"([^"]*)"/i

// Only allow link schemes that can't execute script. Internal links (root-relative) are
// always safe; external links must use an explicit safe protocol or be protocol-relative.
const SAFE_EXTERNAL_HREF = /^(https?:|mailto:|tel:|\/\/)/i

const NAMED_ENTITIES: Record<string, string> = {
    amp: "&",
    lt: "<",
    gt: ">",
    quot: '"',
    apos: "'",
    nbsp: "\u00A0"
}

// Decodes the entities Centra's editor emits. Decoding only ever produces plain text — React
// escapes it on render — so an entity-encoded "<script>" becomes visible text, never markup.
const decodeEntities = (text: string): string =>
    text.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (whole, body: string) => {
        if (body[0] === "#") {
            const isHex = body[1] === "x" || body[1] === "X"
            const codePoint = isHex
                ? parseInt(body.slice(2), 16)
                : parseInt(body.slice(1), 10)

            return Number.isNaN(codePoint) ? whole : String.fromCodePoint(codePoint)
        }

        return NAMED_ENTITIES[body] ?? whole
    })

interface ElementFrame {
    tag: string
    attrs: string
    children: ReactNode[]
}

const renderLink = (frame: ElementFrame, key: number): ReactNode => {
    const rawHref = frame.attrs.match(HREF_PATTERN)?.[1]
    const href = rawHref ? decodeEntities(rawHref).trim() : ""
    const title = frame.attrs.match(TITLE_PATTERN)?.[1]

    if (href && isInternalLink(href)) {
        return (
            <DynamicLink
                key={key}
                href={href}
                title={title}
                prefetch={false}
                className={LINK_CLASS}
            >
                {frame.children}
            </DynamicLink>
        )
    }

    if (href && SAFE_EXTERNAL_HREF.test(href)) {
        return (
            <a
                key={key}
                href={href}
                title={title}
                target="_blank"
                rel="noopener noreferrer"
                className={LINK_CLASS}
            >
                {frame.children}
            </a>
        )
    }

    // No href or an unsafe scheme (javascript:, data:, …) — keep the text, drop the link.
    return createElement("span", { key }, ...frame.children)
}

// Turns a finished element frame into React nodes for its parent. Allowlisted tags become the
// matching element; anything else is unwrapped so its content survives but the tag is dropped.
const closeFrame = (
    frame: ElementFrame,
    key: number,
    remapHeading?: (tag: string) => string
): ReactNode[] => {
    if (frame.tag === "a") return [renderLink(frame, key)]

    if (AUTHOR_HEADING_TAGS.has(frame.tag)) {
        if (!hasVisibleText(frame.children)) return []
        const tag = remapHeading?.(frame.tag) ?? frame.tag
        return [
            createElement(
                tag,
                { key, className: TAG_CLASS[frame.tag] },
                ...frame.children
            )
        ]
    }

    if (INLINE_TAGS.has(frame.tag) || BLOCK_TAGS.has(frame.tag)) {
        if (
            (frame.tag === "h2" || frame.tag === "h3" || frame.tag === "h4") &&
            !hasVisibleText(frame.children)
        ) {
            return []
        }

        return [
            createElement(
                frame.tag,
                { key, className: TAG_CLASS[frame.tag] },
                ...frame.children
            )
        ]
    }

    return frame.children
}

const findOpenFrame = (stack: ElementFrame[], tag: string): number => {
    for (let i = stack.length - 1; i >= 1; i--) {
        if (stack[i].tag === tag) return i
    }

    return -1
}

// Parses a Centra HTML fragment into a React node tree, emitting only allowlisted elements and
// converting anchors into in-app or external links. Malformed/misnested markup is closed
// gracefully so no text content is ever lost.
const parseRichText = (html: string, baseHeadingLevel: HeadingLevel): ReactNode[] => {
    const root: ElementFrame = { tag: "", attrs: "", children: [] }
    const stack: ElementFrame[] = [root]
    let keySeq = 0
    let lastIndex = 0
    let match: RegExpExecArray | null
    const minAuthorLevel = minHeadingInHtml(html)
    const remapHeading = (tag: string) => {
        if (!minAuthorLevel || (tag !== "h2" && tag !== "h3" && tag !== "h4")) {
            return tag
        }
        return remapAuthorHeadingLevel(tag, minAuthorLevel, baseHeadingLevel)
    }

    TAG_PATTERN.lastIndex = 0

    const pushText = (raw: string): void => {
        if (!raw) return

        const decoded = decodeEntities(raw)
        const top = stack[stack.length - 1]

        // Drop whitespace-only text sitting directly between top-level blocks, matching the
        // previous paragraph-based rendering (which trimmed inter-block gaps).
        if (top === root && decoded.trim() === "") return

        top.children.push(decoded)
    }

    while ((match = TAG_PATTERN.exec(html)) !== null) {
        const [, closing, rawName, attrs, selfClose] = match
        const tag = rawName.toLowerCase()

        if (match.index > lastIndex) pushText(html.slice(lastIndex, match.index))
        lastIndex = match.index + match[0].length

        if (closing) {
            const openIndex = findOpenFrame(stack, tag)

            if (openIndex === -1) continue

            while (stack.length - 1 >= openIndex) {
                const frame = stack.pop() as ElementFrame
                stack[stack.length - 1].children.push(
                    ...closeFrame(frame, keySeq++, remapHeading)
                )
            }

            continue
        }

        if (VOID_TAGS.has(tag)) {
            stack[stack.length - 1].children.push(createElement(tag, { key: keySeq++ }))
            continue
        }

        if (selfClose) {
            stack[stack.length - 1].children.push(
                ...closeFrame({ tag, attrs, children: [] }, keySeq++, remapHeading)
            )
            continue
        }

        stack.push({ tag, attrs, children: [] })
    }

    if (lastIndex < html.length) pushText(html.slice(lastIndex))

    while (stack.length > 1) {
        const frame = stack.pop() as ElementFrame
        stack[stack.length - 1].children.push(
            ...closeFrame(frame, keySeq++, remapHeading)
        )
    }

    return root.children
}

export default function CentraRichText({
    content,
    className,
    baseHeadingLevel = "h2"
}: CentraRichTextProps) {
    const nodes = useMemo(
        () => (content ? parseRichText(content, baseHeadingLevel) : []),
        [content, baseHeadingLevel]
    )

    if (nodes.length === 0) return null

    return (
        <div
            className={clsx(
                "text-col-text-secondary Trade_Gothic_Text_16_150_0_300",
                "[&_p:last-child]:mb-0 [&_p]:mb-16",
                "[&_ol:last-child]:mb-0 [&_ol]:mb-16 [&_ol]:list-decimal [&_ol]:pl-20",
                "[&_ul:last-child]:mb-0 [&_ul]:mb-16 [&_ul]:list-disc [&_ul]:pl-20",
                "[&_li:last-child]:mb-0 [&_li]:mb-4",
                className
            )}
        >
            {nodes}
        </div>
    )
}
